import { useRef, useState, memo, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useDispatch, useSelector } from "react-redux";
import { updatePostAction } from "../../redux/actions/postActions";
import { getFileUploadSettings } from "../../redux/api/userAPI";
import { useToast } from "../../contexts/ToastContext";
import { FaGlobe, FaUserFriends, FaUsers, FaLock } from "react-icons/fa";

const EditPostModal = memo(({ showModal, post, onClose }) => {
  const dispatch = useDispatch();
  const { error: showError, success: showSuccess } = useToast();
  const { joinedCommunities } = useSelector((state) => state.community || {});

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    content: post?.content || "",
    audience: post?.audience || "Private",
    selectedCommunities: post?.communities?.map(c => c._id) || [],
    file: null,
    error: "",
    allowComments: post?.allowComments !== undefined ? post.allowComments : true,
  });

  const [fileUploadSettings, setFileUploadSettings] = useState({
    maxFileSizeMB: null,
    allowedFileTypes: []
  });


  // Fetch file upload settings on component mount
  useEffect(() => {
    const fetchFileUploadSettings = async () => {
      try {
        const { error, data } = await getFileUploadSettings();
        if (!error && data) {
          setFileUploadSettings({
            maxFileSizeMB: data.maxFileSizeMB,
            allowedFileTypes: data.allowedFileTypes
          });
        }
      } catch (error) {
        console.error("Error fetching file upload settings:", error);
      }
    };

    fetchFileUploadSettings();
  }, []);

  const handleContentChange = (event) => {
    setFormData({
      ...formData,
      content: event.target.value,
    });
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    // Check if settings are loaded
    if (!fileUploadSettings.maxFileSizeMB || !fileUploadSettings.allowedFileTypes.length) {
      showError("File upload settings are still loading. Please wait a moment and try again.");
      return;
    }

    const maxSizeBytes = fileUploadSettings.maxFileSizeMB * 1024 * 1024;
    
    // Check file size
    if (selectedFile.size > maxSizeBytes) {
      const errorMessage = `Please select a file under ${fileUploadSettings.maxFileSizeMB}MB.`;
      setFormData({
        ...formData,
        file: null,
        error: errorMessage,
      });
      showError(errorMessage);
      return;
    }

    // Check file type
    const fileType = selectedFile.type.split('/')[0];
    if (!fileUploadSettings.allowedFileTypes.includes(fileType)) {
      const allowedTypes = fileUploadSettings.allowedFileTypes.join(' or ');
      const errorMessage = `Please select a ${allowedTypes} file.`;
      setFormData({
        ...formData,
        file: null,
        error: errorMessage,
      });
      showError(errorMessage);
      return;
    }

    // File is valid
    setFormData({
      ...formData,
      file: selectedFile,
      error: "",
    });
  };

  const handleRemoveFile = () => {
    setFormData({
      ...formData,
      file: null,
      error: "",
    });
  };

  const handleCommunityToggle = (communityId) => {
    setFormData(prev => {
      const isSelected = prev.selectedCommunities.includes(communityId);
      return {
        ...prev,
        selectedCommunities: isSelected
          ? prev.selectedCommunities.filter(id => id !== communityId)
          : [...prev.selectedCommunities, communityId]
      };
    });
  };

  const handleAudienceChange = (audience) => {
    setFormData(prev => ({
      ...prev,
      audience,
      selectedCommunities: audience === "Communities" ? prev.selectedCommunities : []
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { content, file, audience, selectedCommunities, allowComments } = formData;
    
    if (loading) return;

    if (!content.trim() && !file) {
      setFormData(prev => ({
        ...prev,
        error: "Please enter a message or select a file."
      }));
      return;
    }

    // Validate community selection for "Communities" audience
    if (audience === "Communities" && (!selectedCommunities || selectedCommunities.length === 0)) {
      const errorMessage = "Please select at least one community.";
      showError(errorMessage);
      setFormData(prev => ({
        ...prev,
        error: errorMessage
      }));
      return;
    }

    setLoading(true);

    try {
      const newPost = new FormData();
      newPost.append("content", content);
      if (file) {
        newPost.append("file", file);
      }
      newPost.append("audience", audience);
      newPost.append("allowComments", allowComments);

      if (audience === "Communities" && selectedCommunities.length > 0) {
        newPost.append("selectedCommunities", JSON.stringify(selectedCommunities));
      }

      const result = await dispatch(updatePostAction(post._id, newPost));
      
      if (result && result.success) {
        showSuccess("Post updated successfully!");
        onClose();
      } else {
        const errorMessage = result?.error || "Failed to update post. Please try again.";
        showError(errorMessage);
      }
    } catch (error) {
      showError("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const cancelButtonRef = useRef(null);

  return (
    <Transition.Root show={showModal} as="div">
      <Dialog
        as="div"
        className="relative z-50"
        initialFocus={cancelButtonRef}
        onClose={onClose}
      >
        <Transition.Child
          as="div"
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as="div"
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 font-flex-regular">
                      Edit Post
                    </Dialog.Title>
                    <button
                      onClick={onClose}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <form onSubmit={handleSubmit}>
                    <div className="flex items-start gap-3 mb-1 md:mb-2">
                      <div className="flex-shrink-0">
                        <img
                          src={JSON.parse(localStorage.getItem("profile"))?.user?.avatar || ""}
                          alt="Profile"
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      </div>
                      <div className="flex-1">
                        <textarea
                          className="w-full shadow-sm hover:shadow-md transition-shadow resize-none border border-gray-200 rounded-lg px-4 py-3 text-lg placeholder-gray-500 font-roboto-flex bg-gray-50 focus:bg-white focus:border-gray-300 transition-colors outline-none"
                          name="content"
                          id="content"
                          value={formData.content}
                          onChange={handleContentChange}
                          minLength={1}
                          maxLength={5000}
                          placeholder="What's on your mind?"
                          rows={2}
                          autoFocus
                        />
                        <div className="mt-1">
                          {formData.file ? (
                            // File Preview
                            <div className="space-y-2">
                              <div className="relative">
                                <div className="w-full aspect-[21/9] rounded-lg overflow-hidden border border-gray-200 bg-gray-50 flex items-center justify-center">
                                  {formData.file.type.startsWith('image/') ? (
                                    <img
                                      src={URL.createObjectURL(formData.file)}
                                      alt="Preview"
                                      className="w-full h-full object-cover"
                                    />
                                  ) : formData.file.type.startsWith('video/') ? (
                                    <video
                                      src={URL.createObjectURL(formData.file)}
                                      className="w-full h-full object-cover"
                                      muted
                                    />
                                  ) : (
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      className="h-12 w-12 text-gray-400"
                                      fill="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                                    </svg>
                                  )}
                                </div>
                                <button
                                  type="button"
                                  onClick={handleRemoveFile}
                                  className="absolute top-2 right-2 bg-action text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-action-hover transition-colors"
                                >
                                  ×
                                </button>
                              </div>
                              <div className="space-y-1">
                                <div className="flex justify-between items-center">
                                  <span className="text-sm font-medium text-gray-700 font-roboto-flex">Who can see this post?</span>
                                  <span className="text-xs text-gray-500 font-roboto-flex">
                                    {formData.content.length}/5000
                                  </span>
                                </div>
                                <div className="flex gap-2">
                                  {[
                                    { name: "Followers", icon: FaUserFriends },
                                    { name: "Communities", icon: FaUsers },
                                    { name: "Anyone", icon: FaGlobe },
                                    { name: "Private", icon: FaLock }
                                  ].map(({ name, icon: Icon }) => (
                                    <button
                                      key={name}
                                      type="button"
                                      onClick={() => handleAudienceChange(name)}
                                      className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-colors font-roboto-flex ${
                                        formData.audience === name
                                          ? "bg-primary text-white"
                                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                      }`}
                                    >
                                      <Icon className="w-3 h-3" />
                                      {name}
                                    </button>
                                  ))}
                                </div>
                                {/* Communities Selection */}
                                {formData.audience === "Communities" && joinedCommunities && joinedCommunities.length > 0 && (
                                  <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                                    <div className="text-xs font-medium text-gray-700 font-roboto-flex mb-2">
                                      Select communities:
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                      {joinedCommunities.map((community) => (
                                        <button
                                          key={community._id}
                                          type="button"
                                          onClick={() => handleCommunityToggle(community._id)}
                                          className={`px-2 py-1 rounded-full text-xs font-medium transition-colors font-roboto-flex border ${
                                            formData.selectedCommunities.includes(community._id)
                                              ? "bg-primary text-white border-primary"
                                              : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                                          }`}
                                        >
                                          {community.name}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Comments Toggle */}
                                <div className="flex items-center justify-between mt-3 p-3 bg-gray-50 rounded-lg">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-gray-700 font-roboto-flex">Don't allow comments</span>
                                    <span className="text-xs text-gray-500 font-roboto-flex">
                                      {!formData.allowComments ? "Comments are disabled" : "Users can comment on this post"}
                                    </span>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => setFormData(prev => ({ ...prev, allowComments: !prev.allowComments }))}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                                      !formData.allowComments ? 'bg-primary-600' : 'bg-gray-200'
                                    }`}
                                  >
                                    <span
                                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                        !formData.allowComments ? 'translate-x-6' : 'translate-x-1'
                                      }`}
                                    />
                                  </button>
                                </div>
                              </div>
                            </div>
                          ) : (
                            // Add photo/video button
                            <div className="space-y-2">
                              <div className="flex justify-between items-center">
                                <label
                                  htmlFor="file"
                                  className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 rounded-lg px-3 py-1 transition-colors"
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-4 w-4 text-green-500"
                                    fill="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                                  </svg>
                                  <span className="text-sm font-medium text-gray-700 font-roboto-flex">Add photo/video</span>
                                </label>
                                <span className="text-xs text-gray-500 font-roboto-flex">
                                  {formData.content.length}/5000
                                </span>
                              </div>
                              <div className="space-y-1">
                                <span className="text-sm font-medium text-gray-700 font-roboto-flex">Who can see this post?</span>
                                <div className="flex gap-2">
                                  {[
                                    { name: "Followers", icon: FaUserFriends },
                                    { name: "Communities", icon: FaUsers },
                                    { name: "Anyone", icon: FaGlobe },
                                    { name: "Private", icon: FaLock }
                                  ].map(({ name, icon: Icon }) => (
                                    <button
                                      key={name}
                                      type="button"
                                      onClick={() => handleAudienceChange(name)}
                                      className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-colors font-roboto-flex ${
                                        formData.audience === name
                                          ? "bg-primary text-white"
                                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                      }`}
                                    >
                                      <Icon className="w-3 h-3" />
                                      {name}
                                    </button>
                                  ))}
                                </div>
                                {/* Communities Selection */}
                                {formData.audience === "Communities" && joinedCommunities && joinedCommunities.length > 0 && (
                                  <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                                    <div className="text-xs font-medium text-gray-700 font-roboto-flex mb-2">
                                      Select communities:
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                      {joinedCommunities.map((community) => (
                                        <button
                                          key={community._id}
                                          type="button"
                                          onClick={() => handleCommunityToggle(community._id)}
                                          className={`px-2 py-1 rounded-full text-xs font-medium transition-colors font-roboto-flex border ${
                                            formData.selectedCommunities.includes(community._id)
                                              ? "bg-primary text-white border-primary"
                                              : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                                          }`}
                                        >
                                          {community.name}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Comments Toggle */}
                                <div className="flex items-center justify-between mt-3 p-3 bg-gray-50 rounded-lg">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-gray-700 font-roboto-flex">Don't allow comments</span>
                                    <span className="text-xs text-gray-500 font-roboto-flex">
                                      {!formData.allowComments ? "Comments are disabled" : "Users can comment on this post"}
                                    </span>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => setFormData(prev => ({ ...prev, allowComments: !prev.allowComments }))}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                                      !formData.allowComments ? 'bg-primary-600' : 'bg-gray-200'
                                    }`}
                                  >
                                    <span
                                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                        !formData.allowComments ? 'translate-x-6' : 'translate-x-1'
                                      }`}
                                    />
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <input
                      name="file"
                      type="file"
                      id="file"
                      accept={fileUploadSettings.allowedFileTypes.length > 0 ? fileUploadSettings.allowedFileTypes.map(type => `${type}/*`).join(', ') : ''}
                      onChange={handleFileChange}
                      disabled={!fileUploadSettings.maxFileSizeMB || !fileUploadSettings.allowedFileTypes.length}
                      className="hidden"
                    />
                    
                    {/* Error Message */}
                    {formData.error && (
                      <div className="text-action text-sm font-roboto-flex mt-2">
                        {formData.error}
                      </div>
                    )}
                    
                    <div className="border-t border-gray-200 mt-4 pt-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-6">
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 rounded-lg text-sm font-medium transition-colors bg-gray-200 text-gray-700 hover:bg-gray-300 font-roboto-flex"
                          >
                            Cancel
                          </button>
                          {(formData.content.trim() || formData.file) && (
                            <button
                              type="submit"
                              disabled={loading}
                              className="px-6 py-2 rounded-lg text-sm font-medium transition-colors bg-primary-700 text-white hover:bg-primary-800 disabled:opacity-50 font-roboto-flex"
                            >
                              {loading ? "Updating..." : "Update Post"}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
});

EditPostModal.displayName = "EditPostModal";

export default EditPostModal;
